/**
 * emit-gemini.ts — PortableSession -> Gemini CLI chat checkpoint, plus the
 * offline placement recipe under ~/.gemini/tmp/<slug>/.
 *
 * Ported from the validated prototype (`emit/gemini.mjs`).
 *
 * `renderGeminiCheckpoint` produces the "manual chat checkpoint" (layer A) that
 * gemini's `/chat resume <tag>` reads: { history: Content[] } where
 * Content = { role: "user" | "model", parts: Part[] }. Gemini has NO headless
 * resume, so fork.ts writes this into ~/.gemini/tmp/<slug>/checkpoint-<tag>.json
 * (via `placeGeminiCheckpoint`) and prints the manual `/chat resume` steps.
 */

import { writeFileSync, mkdirSync, readFileSync } from "node:fs";
import { homedir } from "node:os";
import { basename, join, resolve, sep } from "node:path";
import type { PortableSession } from "./extractor.js";
import { renderResumeContext } from "./resume-context.js";

// The resume tag ( `/chat resume <TAG>` ). Issue-specific so two forks in the same
// repo don't clobber each other's checkpoint (same slug + tag would overwrite),
// and so the tag names what it is instead of leaking the source transcript format.
// Plain tag (alnum + hyphen) → no URI-encoding needed.
export function geminiResumeTag(issueId: number): string {
  return `sailfish-autofix-${issueId}`;
}

// gemini injects this onto the first functionCall of any model turn that lacks a
// thoughtSignature (historyHardening.js: SYNTHETIC_THOUGHT_SIGNATURE). We write it
// ourselves so the /chat debug export path is correct without relying on hardening.
const THOUGHT_SIG = "skip_thought_signature_validator";

interface GeminiPart {
  text?: string;
  functionCall?: { name: string; args: any; id?: string };
  functionResponse?: { name: string; response: any; id?: string };
  thoughtSignature?: string;
}
interface GeminiContent {
  role: "user" | "model";
  parts: GeminiPart[];
}
export interface GeminiCheckpoint {
  history: GeminiContent[];
}

function toArgs(input: any): any {
  if (input && typeof input === "object" && !Array.isArray(input)) return input;
  if (input === undefined) return {};
  return { value: input }; // genai args must be a struct; wrap non-objects
}

/**
 * Convert a PortableSession into the Gemini `{ history }` checkpoint. Pure: no I/O.
 */
export function renderGeminiCheckpoint(session: PortableSession, orientation?: string): GeminiCheckpoint {
  const timeline = Array.isArray(session.timeline) ? session.timeline : [];

  // Pass 1: map tool_call id -> tool name, so tool_results (which carry only
  // tool_use_id) can recover the matching functionCall name.
  const nameById = new Map<string, string>();
  for (const ev of timeline) {
    if (ev.kind === "tool_call" && ev.tool && ev.tool.id != null) {
      nameById.set(ev.tool.id, ev.tool.name);
    }
  }

  // Pass 2: flatten each timeline entry into { role, part } items in order.
  // Roles collapse to genai's two: user text / tool_result -> "user";
  // assistant text / thinking / tool_call -> "model".
  const items: Array<{ role: "user" | "model"; part: GeminiPart }> = [];
  for (const ev of timeline) {
    switch (ev.kind) {
      case "text": {
        if (typeof ev.text !== "string" || ev.text.length === 0) break;
        const role = ev.role === "assistant" ? "model" : "user";
        items.push({ role, part: { text: ev.text } });
        break;
      }
      case "thinking": {
        // Never map to functionCall, never reuse the Anthropic signature. Demote to a
        // clearly-labelled text part in the model turn (drop entirely if empty).
        if (typeof ev.text !== "string" || ev.text.length === 0) break;
        items.push({ role: "model", part: { text: `[reasoning] ${ev.text}` } });
        break;
      }
      case "tool_call": {
        const t = ev.tool || ({} as NonNullable<typeof ev.tool>);
        const fc: GeminiPart["functionCall"] = { name: t.name ?? "unknown_tool", args: toArgs(t.input) };
        if (t.id != null) fc.id = String(t.id);
        items.push({ role: "model", part: { functionCall: fc } });
        break;
      }
      case "tool_result": {
        const r = ev.result || {};
        const id = r.tool_use_id;
        const name = (id != null ? nameById.get(id) : undefined) ?? "unknown_tool";
        const output = r.content !== undefined ? r.content
          : r.structured !== undefined ? r.structured : "";
        const fr: GeminiPart["functionResponse"] = { name, response: { output } }; // response MUST be an object
        if (id != null) fr.id = String(id);
        items.push({ role: "user", part: { functionResponse: fr } });
        break;
      }
      default:
        break;
    }
  }

  // Pass 3: merge consecutive same-role items into Content turns. Merging runs of one
  // role guarantees the result strictly alternates user/model by construction.
  const history: GeminiContent[] = [];
  for (const it of items) {
    const last = history[history.length - 1];
    if (last && last.role === it.role) last.parts.push(it.part);
    else history.push({ role: it.role, parts: [it.part] });
  }

  // Pass 4: stamp thoughtSignature on the first functionCall part of each model turn.
  for (const c of history) {
    if (c.role !== "model") continue;
    const firstCall = c.parts.find((p) => p.functionCall);
    if (firstCall && firstCall.thoughtSignature === undefined) {
      firstCall.thoughtSignature = THOUGHT_SIG;
    }
  }

  // Trailing USER-side context note (labeled, NOT a fabricated model turn). Appended
  // to the last user turn when there is one (keeps strict user/model alternation),
  // else a fresh user turn.
  const ctx = renderResumeContext(session);
  if (ctx) {
    const last = history[history.length - 1];
    if (last && last.role === "user") last.parts.push({ text: ctx });
    else history.push({ role: "user", parts: [{ text: ctx }] });
  }

  // The fork's worktree/branch interview, injected as the agent's OWN closing
  // turn — a trailing MODEL turn. buildOrientation is already in the agent's
  // voice and carries the "won't switch branches/stash without your say-so"
  // guard, so it needs no wrapper: gemini resumes with the model having greeted
  // the user and waits for their choice (not a role-confused user note). Same
  // convention as the claude-code/goose/opencode greeting. The ctx note above
  // ends on a user turn, so a model turn keeps strict alternation; if the tail
  // is already a model turn (no ctx), merge in to avoid two model turns in a row.
  if (orientation) {
    const last = history[history.length - 1];
    if (last && last.role === "model") last.parts.push({ text: orientation });
    else history.push({ role: "model", parts: [{ text: orientation }] });
  }

  // Guard: history must start with a user turn (loader/hardener expects it).
  if (history.length && history[0].role !== "user") {
    history.unshift({ role: "user", parts: [{ text: "(resumed session)" }] });
  }

  return { history };
}

// slug = lower → [^a-z0-9]→'-' → collapse '-' → trim '-' (empty → "project").
// Matches gemini 0.49.0 `slugify` in core/config/storage.js.
export function slugify(s: string): string {
  const out = String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  return out || "project";
}

export interface GeminiPlacement {
  tag: string;
  slug: string;
  checkpointPath: string;
  geminiDir: string;
}

/**
 * Write the checkpoint into ~/.gemini/tmp/<slug>/checkpoint-<tag>.json and seed the
 * project markers so `/chat resume <tag>` finds it without a prior `gemini` run
 * (the offline-seed recipe). All writes are confined
 * under ~/.gemini/.
 */
export function placeGeminiCheckpoint(
  gitRoot: string,
  checkpoint: GeminiCheckpoint,
  tag: string,
  alsoUnder: string[] = [],
): GeminiPlacement {
  const geminiRoot = join(homedir(), ".gemini");
  const confined = geminiRoot + sep;
  const assertConfined = (p: string): string => {
    if (!resolve(p).startsWith(confined)) {
      throw new Error(`Refusing to write outside ~/.gemini/: ${p}`);
    }
    return p;
  };

  const registryPath = assertConfined(join(geminiRoot, "projects.json"));
  let registry: { projects: Record<string, string> } = { projects: {} };
  try {
    const parsed = JSON.parse(readFileSync(registryPath, "utf8"));
    if (parsed && typeof parsed === "object" && parsed.projects && typeof parsed.projects === "object") {
      registry = parsed;
    }
  } catch {
    // no (or unreadable) registry yet — start fresh
  }

  const body = JSON.stringify(checkpoint, null, 2) + "\n";
  const placeOne = (cwd: string): { slug: string; checkpointPath: string; tmpDir: string } => {
    const slug = slugify(basename(cwd));
    const tmpDir = join(geminiRoot, "tmp", slug);
    const historyDir = join(geminiRoot, "history", slug);
    mkdirSync(assertConfined(tmpDir), { recursive: true });
    mkdirSync(assertConfined(historyDir), { recursive: true });
    // Ownership markers: each base dir's .project_root holds the normalized abs cwd.
    writeFileSync(assertConfined(join(tmpDir, ".project_root")), cwd);
    writeFileSync(assertConfined(join(historyDir, ".project_root")), cwd);
    registry.projects[cwd] = slug; // registry mapping: { <abs-cwd>: <slug> }
    const checkpointPath = assertConfined(join(tmpDir, `checkpoint-${tag}.json`));
    writeFileSync(checkpointPath, body);
    return { slug, checkpointPath, tmpDir };
  };

  // Gemini locates a /chat checkpoint by slugify(basename(the dir it's launched
  // in)) — NOT the git root. We can't know which dir the user will run `gemini`
  // from, so seed the checkpoint under every plausible slug: the repo root
  // (primary — full-repo context) PLUS wherever `fork` was invoked (users often
  // just run `gemini` there without cd-ing to the root). Dedup by slug.
  const primary = placeOne(resolve(gitRoot));
  const seen = new Set([primary.slug]);
  for (const d of alsoUnder.map((x) => resolve(x))) {
    const s = slugify(basename(d));
    if (seen.has(s)) continue;
    seen.add(s);
    placeOne(d);
  }

  writeFileSync(registryPath, JSON.stringify(registry, null, 2));
  return { tag, slug: primary.slug, checkpointPath: primary.checkpointPath, geminiDir: primary.tmpDir };
}
